/**
 * Which runner runs what (docs/design/mobile-runs.md, "Routing work by platform"). Each runner platform has
 * its own run queue; a runner serves the platforms in RUNNER_PLATFORMS and says so with a heartbeat in Redis,
 * which the API reads to tell users when nobody can run a project's tests.
 */
import { RUN_QUEUE_NAME, type Platform, type TestType } from './testing.js';
import { isAgentProfile } from './agents.js';

/**
 * `agent`: tests on phones connected through a 1QA Device Agent (any runner can serve it; no Android SDK needed).
 * `perf`: performance runs; the runner only coordinates (the load comes from Load Agents), so any runner can serve it.
 */
export const RUNNER_PLATFORMS = ['web', 'android', 'ios', 'agent', 'perf', 'security'] as const;
export type RunnerPlatform = (typeof RUNNER_PLATFORMS)[number];

export const isRunnerPlatform = (v: unknown): v is RunnerPlatform => typeof v === 'string' && (RUNNER_PLATFORMS as readonly string[]).includes(v);

/** The runner platform that runs a project's tests. */
export function runnerPlatformOf(platform: Platform): RunnerPlatform {
  return platform === 'WEB'
    ? 'web'
    : platform === 'ANDROID'
      ? 'android'
      : platform === 'PERFORMANCE'
        ? 'perf'
        : platform === 'SECURITY'
          ? 'security'
          : 'ios';
}

/** BullMQ queue of a runner platform. Web keeps the original queue name, so jobs queued before routing still run. */
export function queueNameFor(platform: RunnerPlatform): string {
  return platform === 'web' ? RUN_QUEUE_NAME : `${RUN_QUEUE_NAME}-${platform}`;
}

/** BullMQ queue a project's runs and flow runs go to; runs on a Device Agent's phone go to the agent queue. */
export const runQueueFor = (platform: Platform, deviceProfile?: string | null): string =>
  queueNameFor(isAgentProfile(deviceProfile) ? 'agent' : runnerPlatformOf(platform));

/**
 * The runner platform a single test's run goes to. SECURITY scans are heavy and long (a Dockerized AI
 * pentester), so they run on their own `security` platform regardless of the project's platform, never
 * blocking web/mobile test slots. Everything else routes by the project platform (and device profile).
 */
export const runnerPlatformForTest = (platform: Platform, type: TestType, deviceProfile?: string | null): RunnerPlatform =>
  type === 'SECURITY' ? 'security' : isAgentProfile(deviceProfile) ? 'agent' : runnerPlatformOf(platform);

export const RUNNER_PLATFORM_LABEL: Record<RunnerPlatform, string> = { web: 'web', android: 'Android', ios: 'iOS', agent: 'device agent', perf: 'performance', security: 'security' };

/** Redis key of one runner's heartbeat: `<prefix><runnerId>`, a RunnerHeartbeat as JSON. */
export const RUNNER_HEARTBEAT_PREFIX = '1qa:runner:';
/** A runner refreshes its heartbeat this often; a heartbeat expires after RUNNER_HEARTBEAT_TTL_S. */
export const RUNNER_HEARTBEAT_EVERY_MS = 10_000;
export const RUNNER_HEARTBEAT_TTL_S = 30;

export interface RunnerHeartbeat {
  id: string;
  platforms: RunnerPlatform[];
  /** Jobs it runs at once (RUNNER_CONCURRENCY). */
  concurrency: number;
  /** Devices ready for tests, per mobile platform it serves (booting emulators are not counted). */
  devices?: Partial<Record<RunnerPlatform, number>>;
  /** Device profiles runs can choose, per mobile platform (see DeviceProfile). */
  profiles?: Partial<Record<RunnerPlatform, DeviceProfile[]>>;
  startedAt: string;
}

/**
 * A kind of device runs can ask for: an emulator profile (AVD, e.g. "1qa-pixel-9-api36": Pixel 9, Android 16)
 * or a connected phone model ("device:Pixel 7"). Tests of a run that names one only run on such devices.
 */
export interface DeviceProfile {
  id: string;
  /** "Pixel 9 · Android 16". */
  label: string;
  /** Android API level, e.g. 36. */
  sdk: number | null;
  /** Devices of this profile ready now / configured on the runner(s) (booting ones count in total only). */
  ready: number;
  total: number;
}

/** Device profile ids: AVD names and "device:<model>" (letters, digits, space . _ : -). */
export const DEVICE_PROFILE_RE = /^[A-Za-z0-9][A-Za-z0-9 ._:-]{0,99}$/;

const ANDROID_VERSION: Record<number, string> = { 29: '10', 30: '11', 31: '12', 32: '12L', 33: '13', 34: '14', 35: '15', 36: '16', 37: '17' };
export const androidVersionOf = (sdk: number | null): string | null => (sdk === null ? null : (ANDROID_VERSION[sdk] ?? null));

/** "Pixel 9 · Android 16" from an AVD's hardware profile ("pixel_9") and API level. */
export function profileLabel(hardware: string | null, sdk: number | null): string {
  const name = hardware
    ? hardware
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((w) => (/^\d/.test(w) || w.length <= 2 ? w.toUpperCase() : w[0]!.toUpperCase() + w.slice(1)))
        .join(' ')
    : 'Android device';
  const version = androidVersionOf(sdk);
  return version ? `${name} · Android ${version}` : sdk ? `${name} · API ${sdk}` : name;
}

/** GET /runners/status: connected runners per platform. `known: false` when the API cannot read heartbeats. */
export interface RunnerStatus {
  known: boolean;
  /** `devices`: ready devices of mobile runners (always 0 for web); `profiles`: what runs can choose, summed over runners. */
  platforms: Record<RunnerPlatform, { runners: number; devices: number; profiles: DeviceProfile[] }>;
  checkedAt: string;
}

/** Counts heartbeats per platform; malformed entries are ignored. */
export function summarizeRunners(heartbeats: unknown[], now = new Date()): RunnerStatus {
  const platforms = Object.fromEntries(RUNNER_PLATFORMS.map((p) => [p, { runners: 0, devices: 0, profiles: [] as DeviceProfile[] }])) as RunnerStatus['platforms'];
  const count = (v: unknown) => (typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : 0);
  for (const hb of heartbeats) {
    const { platforms: list, devices, profiles } = (hb && typeof hb === 'object' ? hb : {}) as { platforms?: unknown; devices?: unknown; profiles?: unknown };
    if (!Array.isArray(list)) continue;
    for (const p of new Set(list.filter(isRunnerPlatform))) {
      platforms[p].runners++;
      if (p === 'web') continue;
      platforms[p].devices += count(devices && typeof devices === 'object' ? (devices as Record<string, unknown>)[p] : undefined);
      const mine = profiles && typeof profiles === 'object' ? (profiles as Record<string, unknown>)[p] : undefined;
      for (const raw of Array.isArray(mine) ? mine : []) {
        const d = (raw && typeof raw === 'object' ? raw : {}) as Partial<DeviceProfile>;
        if (typeof d.id !== 'string' || !DEVICE_PROFILE_RE.test(d.id)) continue;
        const known = platforms[p].profiles.find((x) => x.id === d.id);
        if (known) {
          known.ready += count(d.ready);
          known.total += count(d.total);
        } else {
          platforms[p].profiles.push({
            id: d.id,
            label: typeof d.label === 'string' ? d.label.slice(0, 100) : d.id,
            sdk: typeof d.sdk === 'number' ? d.sdk : null,
            ready: count(d.ready),
            total: count(d.total),
          });
        }
      }
    }
  }
  for (const p of RUNNER_PLATFORMS) platforms[p].profiles.sort((a, b) => (b.sdk ?? 0) - (a.sdk ?? 0) || a.label.localeCompare(b.label));
  return { known: true, platforms, checkedAt: now.toISOString() };
}

/** A run or flow run still QUEUED this long while no runner serves its platform is marked ERROR. */
export const NO_RUNNER_WAIT_MS = 10 * 60 * 1000;

export const noRunnerMessage = (platform: RunnerPlatform): string =>
  `No ${RUNNER_PLATFORM_LABEL[platform]} runner was connected for ${NO_RUNNER_WAIT_MS / 60_000} minutes, so this run could not start. Ask an admin to start one, then run it again.`;

/** The device a mobile test ran on (TestResult.device), read from the device by the runner. */
export interface TestDevice {
  /** adb serial, e.g. emulator-5554. */
  serial: string;
  /** Product model (ro.product.model), e.g. "Pixel 7"; emulators report their system image ("sdk_gphone64_x86_64"). */
  model: string | null;
  /** OS version, e.g. "14". */
  os: string | null;
  /** Android API level, e.g. 34. */
  sdk: number | null;
  emulator: boolean;
  /** Emulator profile (AVD) name, e.g. "1qa-android-34". */
  avd: string | null;
}

/** "Pixel 7 · Android 14 · R58N123ABC", "Emulator 1qa-android-34 · Android 14 · emulator-5554". */
export function deviceLabel(d: TestDevice): string {
  const name = d.emulator ? `Emulator ${d.avd ?? d.model ?? ''}`.trim() : (d.model ?? 'Android device');
  return [name, d.os ? `Android ${d.os}` : null, d.serial].filter(Boolean).join(' · ');
}
